import { useState, useEffect } from 'react'
import { Navigate } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBookOpen, faClipboardList, faFileContract } from '@fortawesome/free-solid-svg-icons'

import useAxiosPrivate from '../../../hooks/useAxiosPrivate'
import useAuth from '../../../hooks/useAuth'

import Contact from '../../../components/Contact'
import PPQDashboard from './PPQDashboard'
import OHDashboard from './OHDashboard'

const Dashboard = () => {
  const axios = useAxiosPrivate()
  const { auth } = useAuth()

  // Load serviceType from localStorage or default to 'ppq'
  const [serviceType, setServiceType] = useState(() => {
    const saved = localStorage.getItem('oh-dashboard-tab')
    return saved || 'ppq'
  })

  const [previousPolicy, setPreviousPolicy] = useState(false)
  const [orgInfo, setOrgInfo] = useState('')

  const [availableSchools, setAvailableSchools] = useState({
    ppq: { current: [], previous: [] },
    oh: { current: [], previous: [] },
  })

  const [counts, setCounts] = useState({
    ppq: { current: 0, previous: 0 },
    oh: { current: 0, previous: 0 },
  })

  const canAccessPPQ = auth?.user?.oh === 1 || auth?.user?.master_oh === 1
  const canAccessOHA = auth?.user?.oha === 1 || auth?.user?.master_oha === 1
  const canUseOHARequests = canAccessOHA && !!auth?.currentPolicy?.oha_requests
  const canUsePPQ = canAccessPPQ
  const canUseAnyService = canUsePPQ || canUseOHARequests

  useEffect(() => {
    try {
      if (auth?.currentPolicy) {
        setOrgInfo(JSON.parse(localStorage.getItem('orgType')))
        getPreviousPolicy()
      }
    } catch (error) {
      console.error('Error in Dashboard useEffect:', error)
    }
  }, [auth?.currentPolicy])

  useEffect(() => {
    if (!auth?.currentPolicy) return
    fetchSchoolsAndCounts()
  }, [auth?.currentPolicy, previousPolicy, auth?.user?.contact_id, auth?.user?.master_oh, auth?.user?.master_oha])

  useEffect(() => {
    if (!auth?.currentPolicy) return

    if (serviceType === 'ppq' && !canUsePPQ && canUseOHARequests) {
      setServiceType('oh')
      return
    }

    if (serviceType === 'oh' && !canUseOHARequests && canUsePPQ) {
      setServiceType('ppq')
    }
  }, [auth?.currentPolicy, canUsePPQ, canUseOHARequests, serviceType])

  const fetchSchoolsAndCounts = async () => {
    const data = {
      'currentPolicy': auth?.currentPolicy?.policy_id,
      'previousPolicy': previousPolicy?.policy_id,
      'contactId': auth.user.contact_id,
      'masterOh': auth.user.master_oh,
      'masterOha': auth.user.master_oha,
    }

    const response = await axios.post('/service-requests/available-schools', data)

    if (response.status !== 200 || response.data.error === true) {
      console.error('Error fetching available schools:', response.data.message)
      return
    }

    setAvailableSchools({
      ppq: {
        current: response.data.ppq_schools?.current || [],
        previous: response.data.ppq_schools?.previous || []
      },
      oh: {
        current: response.data.oh_schools?.current || [],
        previous: response.data.oh_schools?.previous || []
      }
    })

    setCounts({
      ppq: {
        current: response.data.ppq_counts?.current || 0,
        all: response.data.ppq_counts?.all || 0,
        previous: {
          current: response.data.ppq_counts?.previous.current || 0,
          all: response.data.ppq_counts?.previous.all || 0
        }
      },
      oh: {
        current: response.data.oh_counts?.current || 0,
        all: response.data.oh_counts?.all || 0,
        previous: {
          current: response.data.oh_counts?.previous.current || 0,
          all: response.data.oh_counts?.previous.all || 0
        }
      }
    })
  }

  const getPreviousPolicy = async () => {
    try {
      const response = await axios.post('/staff-hub/previous-policy', {
        token: auth.token,
        orgId: auth.schools[0].id ?? auth?.schools[0].id,
        user: auth?.user?.contact_id,
      })

      if (response.data.error !== true) {
        setPreviousPolicy(response.data.result?.data.policy)
      } else {
        console.error('Error in response:', response.data.message)
      }
    } catch (error) {
      console.error('Error fetching previous policy:', error)
    }
  }

  const handleServiceTypeChange = (newServiceType) => {
    setServiceType(newServiceType)
    localStorage.setItem('oh-dashboard-tab', newServiceType)
  }

  const guideLink = () => {
    let guide = 'https://kisdigital.s3.eu-west-2.amazonaws.com/pdf/OH/OH+Portal+Guide.pdf';
    return guide;
  }

  const processGuideLink = () => {
    let guide = 'https://kisdigital.s3.eu-west-2.amazonaws.com/pdf/OH/OH+Management+Referrals.pdf';
    return guide;
  }

  const termsAndConditionsLink = () => {
    let guide = 'https://kisdigital.s3.eu-west-2.amazonaws.com/pdf/2026/v1/v1-8/TermsAndConditionsOH.pdf';
    if (auth?.currentPolicy.product === 'SWB Premium + OH') {
      guide = 'https://kisdigital.s3.eu-west-2.amazonaws.com/pdf/2026/v1/v1-8/TermsAndConditionsSWBOH.pdf';
    }
    return guide;
  }


  return (
    <div>
      {!!auth?.currentPolicy && !canUseAnyService && <Navigate to="/dashboard" replace />}
      <div className="mb-8 sm:mb-10">
        <h3 className="text-2xl sm:text-3xl md:text-5xl font-bold text-white text-center pt-12">
          {serviceType === 'ppq' ? 'Pre-Placement Questionnaires (PPQ)' : 'Occupational Health Requests'}
        </h3>
        <h4 className="text-lg font-medium text-white text-center">
          {serviceType === 'ppq'
            ? 'Create and Manage your Pre-Placement Questionnaire Requests here.'
            : 'Create and Manage your Occupational Health Requests here.'}
        </h4>
      </div>

      {/* Service Type Toggle */}
      {canUsePPQ && canUseOHARequests && (
        <div className="flex justify-center mb-6">
          <div className="flex bg-gray-100 rounded-lg shadow-md overflow-hidden">
            <button
              className={`px-6 py-3 font-medium transition-colors ${serviceType === 'ppq'
                ? 'bg-white text-[#02ADAD] shadow-sm'
                : 'bg-transparent text-gray-700 hover:bg-gray-50'
                }`}
              onClick={() => handleServiceTypeChange('ppq')}
            >
              Pre-Placement Questionnaires
            </button>
            <button
              className={`px-6 py-3 font-medium transition-colors ${serviceType === 'oh'
                ? 'bg-white text-[#02ADAD] shadow-sm'
                : 'bg-transparent text-gray-700 hover:bg-gray-50'
                }`}
              onClick={() => handleServiceTypeChange('oh')}
            >
              Occupational Health
            </button>
          </div>
        </div>
      )}

      {serviceType === 'ppq' && canUsePPQ ? (
        <PPQDashboard
          previousPolicy={previousPolicy}
          orgInfo={orgInfo}
          availableSchools={availableSchools.ppq}
          counts={counts.ppq}
        />
      ) : null}

      {serviceType === 'oh' && canUseOHARequests ? (
        <OHDashboard
          previousPolicy={previousPolicy}
          orgInfo={orgInfo}
          availableSchools={availableSchools.oh}
          counts={counts.oh}
        />
      ) : null}

      <div className="rounded-md bg-[#FFECD1] p-4 mt-8 border border-[#F8CF94]">
        <div className="flex">
          <div className="flex-shrink-0">
          </div>
          <div className="ml-3">
            <p className="text-sm font-medium text-[#B57B29]">
              <strong>Please Note:</strong> All wellbeing service requests are
              confidential. Therefore any requests for access made by them will
              not show below. Presented here are the {serviceType === 'ppq' ? 'Pre-Placement Questionnaires' : 'Occupational Health Requests'} created by you.
            </p>
          </div>
        </div>
      </div>

      <section className="w-full max-w-[1450px] mx-auto mt-6">
        <div className="bg-white rounded-md shadow-md p-4 sm:p-6">
          <h3 className="text-lg sm:text-xl font-bold text-slate-800">
            Important Resources
          </h3>
          <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <li>
              <a
                href={guideLink()}
                target="_blank"
                rel="noopener noreferrer"
                className="block rounded-md border border-gray-200 px-4 py-3 text-sm font-medium text-[#02ADAD] hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-2"
              >
                <span className="flex items-center gap-3">
                  <FontAwesomeIcon icon={faBookOpen} className="text-base" aria-hidden="true" />
                  <span>OH and PPQ User Guide</span>
                </span>
              </a>
            </li>
            <li>
              <a
                href={processGuideLink()}
                target="_blank"
                rel="noopener noreferrer"
                className="block rounded-md border border-gray-200 px-4 py-3 text-sm font-medium text-[#02ADAD] hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-2"
              >
                <span className="flex items-center gap-3">
                  <FontAwesomeIcon icon={faClipboardList} className="text-base" aria-hidden="true" />
                  <span>OH Management Referral Process Guide</span>
                </span>
              </a>
            </li>
            <li>
              <a
                href={termsAndConditionsLink()}
                target="_blank"
                rel="noopener noreferrer"
                className="block rounded-md border border-gray-200 px-4 py-3 text-sm font-medium text-[#02ADAD] hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-2"
              >
                <span className="flex items-center gap-3">
                  <FontAwesomeIcon icon={faFileContract} className="text-base" aria-hidden="true" />
                  <span>Terms and Conditions</span>
                </span>
              </a>
            </li>
          </ul>
        </div>
      </section>

      <Contact />
    </div>
  )
}

export default Dashboard
